/**
 * Configurazione dell'ambiente.
 *
 * Ordine di precedenza:
 *   1. window.__ZOELIA__  — scritto all'avvio del container (produzione, staging)
 *   2. process.env        — incorporato in compilazione (sviluppo con file .env)
 *   3. valori predefiniti — sviluppo locale senza configurazione
 *
 * L'indirizzo dell'API e' RELATIVO negli ambienti serviti da un solo dominio: frontend e
 * backend stanno sulla stessa origine, con il backend sotto /api. Cosi' la stessa immagine
 * funziona ovunque e non serve nessuna configurazione CORS.
 */
const runtime = (typeof window !== "undefined" && window.__ZOELIA__) || {};

function valore(chiaveRuntime, chiaveCompilazione, predefinito) {
  const r = runtime[chiaveRuntime];
  if (r !== undefined && r !== null && r !== "") return r;
  return process.env[chiaveCompilazione] || predefinito;
}

export const API_BASE_URL = valore("apiUrl", "VUE_APP_API_URL", "http://localhost:8081/api/v1");

/** La radice del backend senza /api/v1: serve a comporre gli indirizzi dei file caricati. */
export const API_ROOT = API_BASE_URL.replace(/\/api\/v1\/?$/, "");

export const KEYCLOAK = {
  url: valore("keycloakUrl", "VUE_APP_KEYCLOAK_URL", "http://localhost:8090"),
  realm: valore("keycloakRealm", "VUE_APP_KEYCLOAK_REALM", "zoelia"),
  clientId: valore("keycloakClientId", "VUE_APP_KEYCLOAK_CLIENT_ID", "zoelia-frontend"),
};

/**
 * La chiave di Google Maps per il completamento automatico dell'indirizzo.
 *
 * Vive nel sorgente della pagina, e non e' un problema: e' limitata per dominio dal pannello
 * di Google, quindi copiata altrove non funziona. Vuota disattiva il completamento e i campi
 * dell'indirizzo restano normali caselle da riempire a mano — che e' il comportamento giusto
 * in sviluppo, dove spesso la chiave non serve.
 */
export const GOOGLE_MAPS_KEY = valore("googleMapsKey", "VUE_APP_GOOGLE_MAPS_KEY", "");

/**
 * L'etichetta con cui questa copia dell'applicazione e' stata DISTRIBUITA.
 *
 * Non quella con cui e' stata costruita: si costruisce solo dalle candidate, e la promozione
 * a finale ri-etichetta l'immagine senza ricompilarla. La versione incorporata nel pacchetto
 * resta quindi per sempre quella della rc, ed e' il motivo per cui la produzione si
 * presentava come "v1.0.0-rc.9" pur girando su v1.0.0.
 *
 * Scritta all'avvio del container insieme a version.json, cosi' le due combaciano.
 */
export const VERSIONE = valore("versione", "VUE_APP_BUILD_VERSION", "sviluppo");

/**
 * Vero solo nella build dell'app mobile (Capacitor), falso nel sito.
 *
 * Lo imposta la build: .env.mobile-staging scrive VUE_APP_PIATTAFORMA=mobile, e vue.config.js
 * da' al sito il valore 'web'. Webpack sostituisce la variabile con il suo testo in fase di
 * compilazione, quindi nel sito questo e' la costante false e ogni ramo che la controlla non
 * viene mai eseguito: per chi usa Zoelia dal browser non cambia nulla.
 *
 * Dove serve che il ramo mobile SPARISCA proprio dal pacchetto del sito (per non spedire il
 * guscio mobile a ogni visitatore), non si passa da questa costante: si scrive la condizione
 * per intero sul posto, perche' webpack sa valutarla solo dentro lo stesso modulo. Succede in
 * layouts/main.vue.
 */
export const PIATTAFORMA_MOBILE = process.env.VUE_APP_PIATTAFORMA === "mobile";

/**
 * L'indirizzo pubblico da cui si aprono i link mandati agli OSPITI (check-in, accesso).
 *
 * Nel sito e' l'origine della pagina, come e' sempre stato. Nell'app no: le pagine arrivano da
 * https://localhost, e un link di check-in costruito su quell'origine sarebbe
 * https://localhost/checkin/<gettone> — che, mandato a un ospite, non apre nulla. Nell'app lo
 * dice la build (VUE_APP_ORIGINE_PUBBLICA), ed e' il dominio dell'ambiente a cui l'app parla.
 */
// La condizione e' scritta per intero e non letta da PIATTAFORMA_MOBILE: cosi' webpack la valuta
// e nel sito il ramo dell'app sparisce, invece di restare nel pacchetto come codice morto.
export const ORIGINE_PUBBLICA = process.env.VUE_APP_PIATTAFORMA === "mobile"
  ? (process.env.VUE_APP_ORIGINE_PUBBLICA || "")
  : (typeof window !== "undefined" ? window.location.origin : "");

export default { API_BASE_URL, API_ROOT, KEYCLOAK, GOOGLE_MAPS_KEY, VERSIONE, PIATTAFORMA_MOBILE, ORIGINE_PUBBLICA };
